import { useState } from 'react';
import { Plus, Search, Link2, Users, Briefcase, Calendar, Music, CreditCard, Heart, Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LINK_CATEGORIES, getCategoryByPlatformId } from '@/data/platforms';
import ImageUpload from './ImageUpload';
import FileDropzone, { PDF_UPLOAD } from './FileDropzone';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = { social: Users, business: Briefcase, meetings: Calendar, music: Music, payment: CreditCard, lifestyle: Heart };

const AddLinkDialog = ({ open, onOpenChange, onPlatformSelect, onCustomLinkSubmit, existingPlatforms = [], mode = 'social' }) => {
  const [selectedCategory, setSelectedCategory] = useState('social');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customForm, setCustomForm] = useState({ title: '', url: '', link_type: 'url', icon: '', thumbnail_url: '', description: '', price: '' });

  const reset = () => {
    setSelectedPlatform(null);
    setLinkUrl('');
    setSearchQuery('');
    setShowCustomForm(false);
    setUploading(false);
    setCustomForm({ title: '', url: '', link_type: 'url', icon: '', thumbnail_url: '', description: '', price: '' });
    setSelectedCategory('social');
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF must be under 20MB');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setLinkUrl(res.data.url);
      toast.success('PDF uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenChange = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handlePlatformSave = () => {
    if (!selectedPlatform || !linkUrl) return;
    onPlatformSelect(selectedPlatform, linkUrl);
    reset();
    onOpenChange(false);
  };

  const handleCustomSave = (e) => {
    e.preventDefault();
    if (!customForm.title || !customForm.url) return;
    let url = customForm.url.trim();
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    onCustomLinkSubmit({
      ...customForm,
      url,
      price: customForm.link_type === 'product' && customForm.price ? parseFloat(customForm.price) : null
    });
    reset();
    onOpenChange(false);
  };

  const filteredPlatforms = selectedCategory
    ? LINK_CATEGORIES[selectedCategory].platforms.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Platform selection view (step 1)
  const renderPlatformPicker = () => (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
        <Input
          data-testid="platform-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-[#1E1E1E] border-[#27272A] text-white"
          placeholder="Search platforms..."
        />
      </div>

      {/* Custom Link button */}
      <button
        data-testid="custom-link-btn"
        onClick={() => setShowCustomForm(true)}
        className="w-full mb-4 p-3 rounded-xl border border-dashed border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 transition-all flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
          <Link2 className="w-4 h-4 text-violet-400" />
        </div>
        <div className="text-left">
          <span className="font-medium text-white text-sm">Custom Link</span>
          <p className="text-xs text-[#71717A]">Add any URL with a custom title</p>
        </div>
      </button>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex-1 overflow-hidden flex flex-col">
        <TabsList className="bg-[#1E1E1E] border border-[#27272A] flex-wrap h-auto gap-1 p-1">
          {Object.entries(LINK_CATEGORIES).map(([id, cat]) => {
            const Icon = CATEGORY_ICONS[id];
            return (
              <TabsTrigger key={id} value={id} className="data-[state=active]:bg-violet-500/20 text-xs px-2 py-1.5 gap-1">
                <Icon className="w-3 h-3" /> {cat.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="flex-1 overflow-y-auto mt-4">
          <div className="grid grid-cols-2 gap-2">
            {filteredPlatforms.map((platform) => {
              const isAdded = existingPlatforms.includes(platform.id);
              return (
                <button
                  key={platform.id}
                  data-testid={`select-${platform.id}`}
                  onClick={() => !isAdded && setSelectedPlatform(platform)}
                  disabled={isAdded}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    isAdded
                      ? 'bg-violet-500/10 border-violet-500/30 opacity-60'
                      : 'bg-[#1E1E1E] border-[#27272A] hover:border-violet-500/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{platform.icon}</span>
                    <span className="font-medium text-white text-sm">{platform.name}</span>
                  </div>
                  {isAdded && <span className="text-xs text-violet-400 mt-1 block">Added</span>}
                </button>
              );
            })}
          </div>
        </div>
      </Tabs>
    </div>
  );

  // Platform URL input (step 2)
  const renderUrlInput = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-[#1E1E1E]">
        <span className="text-3xl">{selectedPlatform.icon}</span>
        <div>
          <h3 className="font-semibold text-white">{selectedPlatform.name}</h3>
          <p className="text-sm text-[#71717A]">{getCategoryByPlatformId(selectedPlatform.id)?.name}</p>
        </div>
      </div>

      {selectedPlatform.supportsUpload && (
        <div data-testid="pdf-upload-section" className="p-4 rounded-xl border border-dashed border-violet-500/40 bg-violet-500/5">
          <Label className="text-[#EDEDED] flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-violet-400" /> Upload PDF
          </Label>
          <FileDropzone
            {...PDF_UPLOAD}
            compact
            testId="pdf-file-dropzone"
            label={linkUrl?.includes('/api/uploads/') ? 'Replace PDF or drag a new file' : 'Choose PDF or drag and drop'}
            disabled={uploading}
            onUploaded={(result) => setLinkUrl(result.url)}
            successMessage="PDF uploaded successfully"
            className="mb-2"
          />
          <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#1E1E1E] border border-[#27272A] cursor-pointer hover:border-violet-500/50 transition-all">
            <Upload className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm text-[#A1A1AA]">{uploading ? 'Uploading...' : linkUrl && linkUrl.includes('/api/uploads/') ? 'PDF uploaded' : 'Choose PDF file'}</span>
            <input data-testid="pdf-file-input" type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} disabled={uploading} />
          </label>
          {linkUrl && linkUrl.includes('/api/uploads/') && (
            <p className="text-xs text-emerald-400 mt-2">PDF ready: {linkUrl.split('/').pop()}</p>
          )}
          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-[#27272A]" />
            <span className="text-xs text-[#71717A]">OR paste a link</span>
            <div className="flex-1 h-px bg-[#27272A]" />
          </div>
        </div>
      )}

      <div>
        <Label className="text-[#EDEDED]">URL / Link</Label>
        <Input
          data-testid="link-url-input"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white"
          placeholder={selectedPlatform.placeholder}
          autoFocus={!selectedPlatform.supportsUpload}
        />
      </div>

      <DialogFooter className="pt-4">
        <Button type="button" variant="outline" onClick={() => setSelectedPlatform(null)}
          className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Back</Button>
        <Button data-testid="save-link-btn" onClick={handlePlatformSave} disabled={!linkUrl || uploading}
          className="bg-violet-500 hover:bg-violet-600 text-white">Add Link</Button>
      </DialogFooter>
    </div>
  );

  // Custom link form (alternative to platform)
  const renderCustomForm = () => (
    <form onSubmit={handleCustomSave} className="space-y-3 overflow-y-auto max-h-[55vh] pr-1">
      <div>
        <Label className="text-[#EDEDED]">Title</Label>
        <Input data-testid="link-title-input" value={customForm.title}
          onChange={(e) => setCustomForm({ ...customForm, title: e.target.value })}
          className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="My Website" required />
      </div>

      <div>
        <Label className="text-[#EDEDED]">URL</Label>
        <Input data-testid="custom-link-url-input" value={customForm.url}
          onChange={(e) => setCustomForm({ ...customForm, url: e.target.value })}
          className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://example.com" required />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#EDEDED]">Link Type</Label>
          <Select value={customForm.link_type} onValueChange={(v) => setCustomForm({ ...customForm, link_type: v })}>
            <SelectTrigger data-testid="link-type-select" className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E1E1E] border-[#27272A] z-[200]" position="popper" sideOffset={4}>
              <SelectItem value="url">Regular Link</SelectItem>
              <SelectItem value="menu">Menu (PDF / Link)</SelectItem>
              <SelectItem value="product">Product / Shop</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#EDEDED]">Icon (optional)</Label>
          <Input data-testid="link-icon-input" value={customForm.icon}
            onChange={(e) => setCustomForm({ ...customForm, icon: e.target.value })}
            className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="🔗" maxLength={2} />
        </div>
      </div>

      {customForm.link_type === 'product' && (
        <div>
          <Label className="text-[#EDEDED]">Price ($)</Label>
          <Input data-testid="link-price-input" value={customForm.price}
            onChange={(e) => setCustomForm({ ...customForm, price: e.target.value })}
            className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="29.99" type="number" step="0.01" />
        </div>
      )}

      <div>
        <Label className="text-[#EDEDED]">Thumbnail Image (optional)</Label>
        <div className="mt-1.5">
          <ImageUpload value={customForm.thumbnail_url} onChange={(url) => setCustomForm({ ...customForm, thumbnail_url: url })} label="Link Thumbnail" shape="rect" />
        </div>
      </div>

      <div>
        <Label className="text-[#EDEDED]">Description (optional)</Label>
        <Textarea data-testid="link-description-input" value={customForm.description}
          onChange={(e) => setCustomForm({ ...customForm, description: e.target.value })}
          className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="Brief description..." rows={2} />
      </div>

      <DialogFooter className="pt-2 sticky bottom-0 bg-[#121212]">
        <Button type="button" variant="outline" onClick={() => setShowCustomForm(false)}
          className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Back</Button>
        <Button data-testid="save-custom-link-btn" type="submit"
          className="bg-violet-500 hover:bg-violet-600 text-white">Add Link</Button>
      </DialogFooter>
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">Add Link</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Choose a platform or add a custom link</DialogDescription>
        </DialogHeader>

        {showCustomForm ? renderCustomForm() : selectedPlatform ? renderUrlInput() : renderPlatformPicker()}
      </DialogContent>
    </Dialog>
  );
};

export default AddLinkDialog;
